import { useState, useEffect } from "react";
import { Menu, X, ChevronDown, Shield, HeartPulse, Package, Sparkles, Zap, Briefcase, Building2, Clock, Wrench, Paintbrush, Bike, Landmark } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import logoDark from "@/assets/logo-dark.png";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

const brandNames = [
  { main: "Sustainable Development", sub: "House of Mobility" },
  { main: "تطوير الاستدامة", sub: "هاوس أوف موبايلتي", isArabic: true },
];

const productCategories = [
  { name: "Security & Patrol", icon: Shield, href: "/products", description: "Law enforcement & patrol vehicles", gradient: "from-sky-blue/20 to-sky-blue/5" },
  { name: "Medical & Emergency", icon: HeartPulse, href: "/products", description: "Emergency response vehicles", gradient: "from-rose/20 to-rose/5" },
  { name: "Logistics & Utility", icon: Package, href: "/products", description: "Industrial & utility vehicles", gradient: "from-amber-glow/20 to-amber-glow/5" },
  { name: "Events & Hospitality", icon: Sparkles, href: "/products", description: "Premium event mobility", gradient: "from-purple/20 to-purple/5" },
  { name: "Personal Mobility", icon: Zap, href: "/products", description: "EUCs, scooters & e-bikes", gradient: "from-eco-green/20 to-eco-green/5" },
];

const serviceItems = [
  { name: "B2B Solutions", icon: Briefcase, href: "/services/b2b-solutions", description: "Corporate mobility partner" },
  { name: "B2G Services", icon: Building2, href: "/services/b2g-services", description: "Government & public sector" },
  { name: "Leasing Programs", icon: Clock, href: "/services/leasing-mobility", description: "On-demand fleet leasing" },
  { name: "Service & Maintenance", icon: Wrench, href: "/services/service-maintenance", description: "Authorized service provider" },
  { name: "Fleet Customization", icon: Paintbrush, href: "/services/fleet-customization", description: "Modification & branding" },
  { name: "Motorized Mobility", icon: Bike, href: "/services/motorized-mobility", description: "Electric & combustion" },
  { name: "Mega Projects", icon: Landmark, href: "/services/mega-projects", description: "Run & operate" },
];

const simpleLinks = [
  { name: "Sustainability", href: "/sustainability" },
  { name: "About", href: "/about" },
  { name: "Projects", href: "/projects" },
  { name: "Careers", href: "/careers" },
  { name: "Contact", href: "/contact" },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [brandIndex, setBrandIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 80);
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsFading(true);
      setTimeout(() => {
        setBrandIndex((prev) => (prev + 1) % brandNames.length);
        setIsFading(false);
      }, 300);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const isActive = (href: string) => location.pathname === href || location.pathname.startsWith(href + "/");

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled || isOpen ? "glass-vibrant" : "bg-transparent"
    }`}>
      {/* Gradient accent line */}
      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-eco-green via-teal-bright to-sky-blue transition-opacity duration-300 ${
        isScrolled ? "opacity-80" : "opacity-0"
      }`} />

      <div className={`container mx-auto px-6 transition-all duration-300 ${isScrolled ? "py-3" : "py-4"}`}>
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className={`rounded-full overflow-hidden bg-white/10 flex items-center justify-center transition-all duration-300 group-hover:scale-110 ${
              isScrolled ? "w-10 h-10" : "w-12 h-12"
            }`}>
              <img src={logoDark} alt="SDC Logo" className={`object-contain transition-all duration-300 ${isScrolled ? "w-8 h-8" : "w-10 h-10"}`} />
            </div>
            <div className="overflow-hidden">
              <h1 className={`text-lg font-bold text-foreground group-hover:text-primary transition-all duration-300 ${
                brandNames[brandIndex].isArabic ? "font-arabic" : ""
              } ${isFading ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"}`}>
                {brandNames[brandIndex].main}
              </h1>
              <p className={`text-xs text-muted-foreground transition-all duration-300 ${
                brandNames[brandIndex].isArabic ? "font-arabic" : ""
              } ${isFading ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"}`}>
                {brandNames[brandIndex].sub}
              </p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            <NavigationMenu>
              <NavigationMenuList>
                {/* Home */}
                <NavigationMenuItem>
                  <Link to="/" className={`relative text-sm font-medium px-3 py-2 transition-colors duration-300 ${
                    location.pathname === "/" ? "text-primary" : "text-muted-foreground hover:text-foreground"
                  }`}>
                    Home
                  </Link>
                </NavigationMenuItem>

                {/* Products Mega Menu */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger className={`text-sm font-medium bg-transparent hover:bg-transparent data-[state=open]:bg-transparent ${
                    isActive("/products") ? "text-primary" : "text-muted-foreground hover:text-foreground"
                  }`}>
                    Products
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[480px] p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-foreground">Product Categories</h3>
                        <Link to="/products" className="text-xs text-primary hover:underline">View all →</Link>
                      </div>
                      <div className="grid gap-2">
                        {productCategories.map((cat) => (
                          <NavigationMenuLink asChild key={cat.name}>
                            <Link
                              to={cat.href}
                              className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-secondary/50 transition-colors group"
                            >
                              <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${cat.gradient} flex items-center justify-center`}>
                                <cat.icon className="w-4 h-4 text-foreground" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">{cat.name}</p>
                                <p className="text-xs text-muted-foreground">{cat.description}</p>
                              </div>
                            </Link>
                          </NavigationMenuLink>
                        ))}
                      </div>
                      <div className="mt-3 pt-3 border-t border-border">
                        <Link to="/products/customization" className="text-xs text-muted-foreground hover:text-primary transition-colors">
                          🎨 Custom fleet solutions available →
                        </Link>
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Services Mega Menu */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger className={`text-sm font-medium bg-transparent hover:bg-transparent data-[state=open]:bg-transparent ${
                    isActive("/services") ? "text-primary" : "text-muted-foreground hover:text-foreground"
                  }`}>
                    Services
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[520px] p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-foreground">Our Services</h3>
                        <Link to="/services" className="text-xs text-primary hover:underline">View all →</Link>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {serviceItems.map((svc) => (
                          <NavigationMenuLink asChild key={svc.name}>
                            <Link
                              to={svc.href}
                              className="flex items-center gap-2.5 p-2.5 rounded-lg hover:bg-secondary/50 transition-colors group"
                            >
                              <svc.icon className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">{svc.name}</p>
                                <p className="text-xs text-muted-foreground truncate">{svc.description}</p>
                              </div>
                            </Link>
                          </NavigationMenuLink>
                        ))}
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Simple links */}
                {simpleLinks.map((link) => (
                  <NavigationMenuItem key={link.name}>
                    <Link to={link.href} className={`relative text-sm font-medium px-3 py-2 transition-colors duration-300 ${
                      isActive(link.href) ? "text-primary" : "text-muted-foreground hover:text-foreground"
                    }`}>
                      {link.name}
                    </Link>
                  </NavigationMenuItem>
                ))}
              </NavigationMenuList>
            </NavigationMenu>

            {/* Get a Quote CTA */}
            <Link to="/contact?subject=Quote Request" className="ml-3">
              <Button size="sm" className="rounded-full bg-gradient-to-r from-primary to-accent hover:opacity-90 shadow-lg text-primary-foreground font-semibold text-xs px-5">
                Get a Quote
              </Button>
            </Link>
          </div>

          {/* Mobile: CTA + Hamburger */}
          <div className="flex lg:hidden items-center gap-2">
            <Link to="/contact?subject=Quote Request">
              <Button size="sm" className="rounded-full bg-gradient-to-r from-primary to-accent text-primary-foreground text-xs px-4 h-8">
                Quote
              </Button>
            </Link>
            <button
              className="text-foreground p-2 hover:bg-secondary/50 rounded-lg transition-colors"
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-border pt-4 animate-fade-in">
            <div className="flex flex-col gap-1">
              <Link to="/" className={`py-2 px-3 rounded-lg transition-colors ${location.pathname === "/" ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"}`} onClick={() => setIsOpen(false)}>
                Home
              </Link>

              {/* Products accordion */}
              <button onClick={() => setProductsOpen(!productsOpen)} className="flex items-center justify-between py-2 px-3 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors">
                <span className={isActive("/products") ? "text-primary" : ""}>Products</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${productsOpen ? "rotate-180" : ""}`} />
              </button>
              {productsOpen && (
                <div className="pl-4 space-y-1 animate-fade-in">
                  <Link to="/products" className="block py-1.5 px-3 text-sm text-muted-foreground hover:text-foreground rounded" onClick={() => setIsOpen(false)}>All Products</Link>
                  {productCategories.map((cat) => (
                    <Link key={cat.name} to={cat.href} className="flex items-center gap-2 py-1.5 px-3 text-sm text-muted-foreground hover:text-foreground rounded" onClick={() => setIsOpen(false)}>
                      <cat.icon className="w-3.5 h-3.5" />
                      {cat.name}
                    </Link>
                  ))}
                </div>
              )}

              {/* Services accordion */}
              <button onClick={() => setServicesOpen(!servicesOpen)} className="flex items-center justify-between py-2 px-3 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors">
                <span className={isActive("/services") ? "text-primary" : ""}>Services</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
              </button>
              {servicesOpen && (
                <div className="pl-4 space-y-1 animate-fade-in">
                  <Link to="/services" className="block py-1.5 px-3 text-sm text-muted-foreground hover:text-foreground rounded" onClick={() => setIsOpen(false)}>All Services</Link>
                  {serviceItems.map((svc) => (
                    <Link key={svc.name} to={svc.href} className="flex items-center gap-2 py-1.5 px-3 text-sm text-muted-foreground hover:text-foreground rounded" onClick={() => setIsOpen(false)}>
                      <svc.icon className="w-3.5 h-3.5" />
                      {svc.name}
                    </Link>
                  ))}
                </div>
              )}

              {simpleLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`py-2 px-3 rounded-lg transition-colors ${
                    isActive(link.href) ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
